import React, { FC, useState, useEffect } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  useUpdateReviewMutation,
  useDeleteReviewPhotoMutation,
  ReviewsQuery,
} from "../../graphql/reviews.hooks";
import { Formik, Form, Field } from "formik";
import { StarRating } from "../../pages/reviews/StarRating";
import { Download, XCircle } from "react-bootstrap-icons";

type Props = {
  showModal: boolean;
  onCancel?: () => void;
  reviews: ReviewsQuery["user"]["reviews"]["items"][number][];
  uuid: string;
};

type Photo = {
  id: number;
  url: string;
  file?: File;
};
type initialValues = {
  overallRating: number;
  foodRating: number;
  serviceRating: number;
  atmosphereRating: number;
  billPerPerson: string;
  comment: string;
  photos: Photo[];
};
export enum ReviewBillPerPerson {
  From_1 = "FROM_1",
  From_10 = "FROM_10",
  From_50 = "FROM_50",
  From_100 = "FROM_100",
}
export const EditReviewModal: FC<Props> = ({
  showModal,
  onCancel,
  reviews,
  uuid,
}) => {
  const { t } = useTranslation();
  const currentReview = reviews?.find((review) => review.uuid === uuid);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<number[]>([]);

  const [updateReview] = useUpdateReviewMutation();
  const [deleteReviewPhoto] = useDeleteReviewPhotoMutation();

  useEffect(() => {
    if (currentReview?.photos && Array.isArray(currentReview?.photos)) {
      setPhotos(
        currentReview.photos.map((photo) => ({
          id: photo.id,
          url: photo.url,
        }))
      );
      setDeletedPhotoIds([]);
    } else {
      setPhotos([]);
      setDeletedPhotoIds([]);
    }
  }, [currentReview, showModal]);

  const initialValues = {
    overallRating: currentReview?.overallRating,
    foodRating: currentReview?.foodRating || 0,
    serviceRating: currentReview?.serviceRating || 0,
    atmosphereRating: currentReview?.atmosphereRating || 0,
    billPerPerson: currentReview?.billPerPerson || "",
    comment: currentReview?.comment || "",
    photos: currentReview?.photos || [],
  };

  const handleAddPhotos = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: Photo[]) => void
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newPhotos: Photo[] = [];
    const maxPhotos = 8 - photos.length;

    for (let i = 0; i < Math.min(files.length, maxPhotos); i++) {
      const file = files[i];
      if (file.size > 20 * 1024 * 1024) {
        alert(`File ${file.name} exceeds 20 MB limit.`);
        continue;
      }
      const id = Date.now() + i;
      const url = URL.createObjectURL(file);
      newPhotos.push({ id, url, file });
    }

    setPhotos([...photos, ...newPhotos]);
    setFieldValue("photos", [...photos, ...newPhotos]);
  };

  const handleDeletePhoto = (photoId: number) => {
    const photoToDelete = photos.find((photo) => photo.id === photoId);
    if (photoToDelete && !photoToDelete.file) {
      setDeletedPhotoIds([...deletedPhotoIds, photoId]);
    }
    setPhotos(photos.filter((photo) => photo.id !== photoId));
  };

  useEffect(() => {
    return () => {
      photos.forEach((photo) => {
        if (photo.file) URL.revokeObjectURL(photo.url);
      });
    };
  }, [photos]);

  const handleCancel = () => {
    setPhotos([]);
    setDeletedPhotoIds([]);
    if (onCancel) onCancel();
  };

  const handleSubmit = async (values: initialValues) => {
    try {
      for (const photoId of deletedPhotoIds) {
        await deleteReviewPhoto({
          variables: {
            reviewUuid: uuid,
            photoId,
          },
        });
      }

      const newPhotos = photos
        .filter((photo) => photo.file)
        .map((photo) => photo.file);

      await updateReview({
        variables: {
          uuid,
          overallRating: values.overallRating,
          atmosphereRating:
            values.atmosphereRating === 0 ? null : values.atmosphereRating,
          foodRating: values.foodRating === 0 ? null : values.foodRating,
          serviceRating:
            values.serviceRating === 0 ? null : values.serviceRating,
          comment: values.comment,
          billPerPerson:
            values.billPerPerson === ""
              ? null
              : (values.billPerPerson as ReviewBillPerPerson),
          photos: newPhotos,
        },
      });

      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal show={showModal} onHide={handleCancel} centered scrollable>
      <Modal.Header className="border-0" closeButton>
        <Modal.Title className="text-dark">{t("review.modalHead")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 text-dark">
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue }) => (
            <Form>
              <div className="px-4 pt-4">
                <div className="border rounded-4 p-3 mb-4">
                  <Row>
                    <Col xs={3}>
                      <div className="rounded-3 overflow-hidden review-img-container">
                        <img
                          src={currentReview?.restaurant.logo}
                          alt="Img-1"
                          className="img-fluid w-100 h-100 restaurant-img"
                          loading="lazy"
                        />
                      </div>
                    </Col>
                    <Col xs={8}>
                      <p className="fw-bold mb-2">
                        {currentReview?.restaurant.name}
                      </p>
                      <p className="mb-0 text-gray">
                        <small>
                          {currentReview?.restaurant.cuisines.map(
                            (cuisine, index) => (
                              <span key={cuisine.id}>
                                {cuisine.name}
                                {index <
                                  currentReview?.restaurant.cuisines.length -
                                    1 && " / "}
                              </span>
                            )
                          )}
                          {currentReview?.restaurant.cuisines.length > 0 &&
                          currentReview?.restaurant.types.length > 0
                            ? " • "
                            : ""}
                          {currentReview?.restaurant.types.map(
                            (type, index) => (
                              <span key={type.id}>
                                {type.name}
                                {index <
                                  currentReview?.restaurant.types.length - 1 &&
                                  " / "}
                              </span>
                            )
                          )}
                        </small>
                      </p>
                    </Col>
                  </Row>
                </div>

                <div className="border rounded-3 p-3 text-center mb-4">
                  <p className="fw-bold fs-7">{t("review.overallRating")}</p>
                  <Field
                    name="overallRating"
                    component={StarRating}
                    starId="overallRating"
                  />
                </div>
                <div className="border rounded-3 p-3 text-center mb-4">
                  <p className="fw-bold fs-7">{t("review.food")}</p>
                  <Field
                    name="foodRating"
                    component={StarRating}
                    starId="foodRating"
                  />
                </div>
                <div className="border rounded-3 p-3 text-center mb-4">
                  <p className="fw-bold fs-7">{t("review.service")}</p>
                  <Field
                    name="serviceRating"
                    component={StarRating}
                    starId="serviceRating"
                  />
                </div>
                <div className="border rounded-3 p-3 text-center mb-4">
                  <p className="fw-bold fs-7">{t("review.atmosphere")}</p>
                  <Field
                    name="atmosphereRating"
                    component={StarRating}
                    starId="atmosphereRating"
                  />
                </div>

                <div className="form-floating mb-4">
                  <Field
                    as="select"
                    name="billPerPerson"
                    className="form-select"
                    id="floatingSelect"
                    aria-label="Floating label select example"
                  >
                    <option value="">{t("review.selectAmount")}</option>
                    <option value="FROM_1">$1–$10</option>
                    <option value="FROM_10">$10–$50</option>
                    <option value="FROM_50">$50–$100</option>
                    <option value="FROM_100">$100+</option>
                  </Field>
                  <label htmlFor="floatingSelect">
                    {t("review.perPerson")}
                  </label>
                </div>

                <div className="form-floating mb-4">
                  <Field
                    as="textarea"
                    name="comment"
                    className="form-control"
                    placeholder={t("review.reviewDetails")}
                    id="floatingTextarea2"
                    style={{ height: "100px" }}
                    maxLength={1000}
                  />
                  <label htmlFor="floatingTextarea2">
                    {t("review.reviewDetails")}
                  </label>
                </div>

                <div className="mb-4">
                  <input
                    type="file"
                    id="imageInput"
                    accept="image/jpeg,image/png"
                    className="d-none"
                    multiple
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleAddPhotos(event, setFieldValue)
                    }
                    disabled={photos.length >= 8}
                    aria-label="Upload Photo"
                  />
                  <label
                    htmlFor="imageInput"
                    className={`w-100 btn btn-primary py-3 bg-primary-light text-primary ${
                      photos.length >= 8 ? "disabled" : ""
                    }`}
                  >
                    <Download className="me-2" />
                    {t("review.addPhoto")}
                  </label>
                  <span className="text-gray fs-7 d-block mt-2">
                    {t("review.addMaximum")}
                  </span>
                </div>

                <div id="thumbnailContainer" className="row g-3 mb-4">
                  {photos.map((photo) => (
                    <div
                      className="col-3 thumbnail-container position-relative"
                      key={photo.id}
                    >
                      <img
                        className="thumbnail img-fluid"
                        src={photo.url}
                        alt={`Img-${photo.id}`}
                      />
                      <button
                        className="remove-btn position-absolute top-0 end-0"
                        onClick={() => handleDeletePhoto(photo.id)}
                        aria-label={`Delete photo ${photo.id}`}
                      >
                        <XCircle className="text-dark" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <Modal.Footer className="d-block m-0 border-0 p-0 sticky-bottom bg-white w-100 px-4 pb-4">
                <Row className="gx-0 m-0">
                  <Col xs={12} sm={6} className="pe-lg-1 mb-3 mb-lg-0">
                    <Button
                      variant="outline-primary"
                      className="w-100 py-3"
                      onClick={handleCancel}
                    >
                      {t("review.modalCancel")}
                    </Button>
                  </Col>
                  <Col xs={12} sm={6} className="ps-lg-1">
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-100 fw-bold text-white py-3"
                    >
                      {t("review.modalSave")}
                    </Button>
                  </Col>
                </Row>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
